import { DEFAULT_GROUP_DATA, SEARCH_BY_FILED, SEARCH_LEVEL, COGNITO_EMAIL_VALIDATION, BRAND_PROFILES } from 'constants';
import isEmpty from 'lodash/isEmpty';
import {
  profileService,
  cmdProfileService,
  cmdProfileServiceV3,
  clientsService,
  clientsCentralSearchService,
  clientsCentralSearchServiceV2,
  groupsService,
  paymentService,
  addressValidationService,
  cancelRequest,
  conversationService,
  groupsV2Service,
  eventDetailsService,
} from 'config/api';
import clientAuth from 'config/clientAuth';
import { convertAurusCardType, getRecentSearches } from 'utils';
import { addNotification } from 'shell/actions';

import { convertToXml, getStandardizeAddress, splitPlccAndNoPlccKiNumbers } from '../reducers/utils';
import actionTypes from './actionTypes';

const MAX_DISPLAY_SEARCH_RESULTS = 1000;

export const getClient = (clientId) => async (dispatch, getState) => {
  const client = getState().clientsModule.clientDetails.data;

  if (client && client.clientId === clientId) {
    dispatch({
      type: actionTypes.CLIENT_DETAILS_SUCCESS,
      payload: client,
    });
    return;
  }

  dispatch({
    type: actionTypes.CLIENT_DETAILS_LOADING,
  });

  try {
    const res = await profileService.get(`customers/${clientId}`, { clientId });

    res.data.clientId = clientId;

    dispatch({
      type: actionTypes.CLIENT_DETAILS_SUCCESS,
      payload: res.data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENT_DETAILS_ERROR,
      payload: error,
    });
  }
};

export const getCMDProfile = (cmdUri, showGroups = false) => async (dispatch, getState) => {
  const { profileData } = getState().clientsModule.clientDetails;

  if (profileData && profileData.cmdUri === cmdUri) {
    dispatch({
      type: actionTypes.CMD_PROFILE_SUCCESS,
      payload: profileData,
    });
    if (showGroups) {
      dispatch({ type: actionTypes.GET_CLIENT_GROUPS_SUCCESS, payload: profileData.groups });
    }
    dispatch({
      type: actionTypes.CLIENT_LOYALTY_SUCCESS,
      payload: profileData.loyaltyData,
    });
    return;
  }

  dispatch({
    type: actionTypes.CMD_PROFILE_LOADING,
  });

  try {
    const res = await cmdProfileServiceV3.get(`profiles/${cmdUri}`);

    dispatch({
      type: actionTypes.CMD_PROFILE_SUCCESS,
      payload: {
        ...res.data,
        cmdUri,
      },
    });

    dispatch({
      type: actionTypes.CLIENT_LOYALTY_SUCCESS,
      payload: res.data.loyaltyData,
    });

    dispatch({ type: actionTypes.GET_CLIENT_BADGES_SUCCESS, payload: res.data.badges });

    dispatch({ type: actionTypes.SET_CLIENT_CONNECTS, payload: res.data.connects });

    if (showGroups && res.data.groups) {
      dispatch({ type: actionTypes.GET_CLIENT_GROUPS_SUCCESS, payload: res.data.groups });
    }
  } catch (error) {
    dispatch({
      type: actionTypes.CMD_PROFILE_ERROR,
      payload: error,
    });
  }
};

export const updateClientWithCMDProfile = (cmdURI) => async (dispatch, getState) => {
  const clients = getState().clientsModule.clientsList.data;
  const { profileData } = getState().clientsModule.clientDetails;
  let needToUpdate = false;
  const updatedClients = clients.map((client) => {
    if (client.profileId === cmdURI && !client.phones && !client.emails) {
      needToUpdate = true;
      return { ...client, ...profileData };
    }
    return client;
  });
  if (needToUpdate) {
    dispatch({
      type: actionTypes.CLIENTS_LIST_SUCCESS,
      payload: updatedClients,
    });
  }
};

export const getAndUpdateClientWithCMDProfile = (cmdURI) => async (dispatch) => {
  await dispatch(getCMDProfile(cmdURI));
  return dispatch(updateClientWithCMDProfile(cmdURI));
};

const createNMAccount = async (params) => {
  return clientAuth.signUp(params);
};

const createBGAccount = async (params) => {
  const { attributes } = params;
  const body = {
    firstName: attributes.given_name,
    lastName: attributes.family_name,
    email: params.username,
  };
  try {
    const res = await profileService.post(`/customers`, body, {
      httpClientOptions: { handleErrors: false },
    });
    return {
      userSub: res.data.profileId,
    };
  } catch (err) {
    const { message } = err.response.data;
    const isExistsMsg = message.includes('already exists');
    const convertedErr = new Error(message);
    if (isExistsMsg) {
      convertedErr.name = COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.EXISTS;
    } else {
      convertedErr.name = COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.INVALID;
    }
    throw convertedErr;
  }
};

export const createOnlineAccount = ({ cmdUri, phone, sourceName, firstName, lastName, ...params }) => async (
  dispatch,
) => {
  dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_LOADING });

  const { username: email } = params;

  try {
    let res = null;
    if (sourceName && sourceName === BRAND_PROFILES.BG) {
      res = await createBGAccount(params);
    } else {
      res = await createNMAccount(params);
    }

    try {
      const body = {
        onlineProfileId: res.userSub,
        cmdUri,
        email,
        sourceName,
        phone,
        firstName,
        lastName,
      };

      const response = await profileService.post(`/profiles`, body);

      const newProfile = {
        profileId: response.data.onlineProfileId,
        phone: response.data.phone,
        sourceName: response.data.sourceName,
        profileType: `${response.data.sourceName}O`,
        email: response.data.email,
        newCreated: true,
      };

      dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_SUCCESS, payload: newProfile });
    } catch (error) {
      dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_ERROR });
    }

    dispatch(
      addNotification({
        id: `create-profile-${Date.now()}`,
        message: 'Online account was successfully created',
        type: 'success',
      }),
    );

    return res;
  } catch (error) {
    try {
      dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_ERROR });
      const { name, message } = error;

      const isExistsMsg = message.includes('already exists');
      const isCMDEmailExists = name === COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.EXISTS || isExistsMsg;
      const isEmailExists = name === COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.INVALID && isExistsMsg;
      const isInvalidEmail = name === COGNITO_EMAIL_VALIDATION.RESPONSE_TYPES.INVALID && !isExistsMsg;

      if (isCMDEmailExists) {
        const onlineProfile = await profileService.post(`/customerByEmail`, { email });

        const { profileId } = onlineProfile.data;

        try {
          const cmdProfile = await cmdProfileService.get(`/profiles/${profileId}?sourceName=${sourceName}`, {
            httpClientOptions: { handleErrors: false },
          });

          return {
            type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.ERROR,
            onlineProfile: onlineProfile.data,
            cmdProfile: cmdProfile.data,
          };
        } catch (e) {
          // Specially for case cases when an online account for a client with a specific email already exists in ATG
          // but it may not have relevant information to tie it a CMD profile
          // More details in https://jira.mynmg.com/browse/OSP-3740
          return {
            type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.ERROR,
            onlineProfile: onlineProfile.data,
            cmdProfile: null,
          };
        }
      }

      if (isEmailExists) {
        return { type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.EXISTED };
      }

      if (isInvalidEmail) {
        return { type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.INVALID };
      }
    } catch (err) {
      dispatch({ type: actionTypes.CREATE_ONLINE_ACCOUNT_ERROR });
      return { profile: null, type: COGNITO_EMAIL_VALIDATION.ERROR_TYPES.ERROR };
    }
  }
};

export const updateOnlineAccount = (
  customerId,
  { firstName, lastName /* TODO: could be extended */ },
  options = {
    brand: BRAND_PROFILES.NM,
    linkToCmd: false,
    cmdUri: null,
  },
) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_ONLINE_ACCOUNT_LOADING });

  try {
    const body = {};

    if (firstName !== undefined) body.firstName = firstName;
    if (lastName !== undefined) body.lastName = lastName;

    const res = await profileService.put(`customers/${customerId}`, body);

    if (options.linkToCmd) {
      const { data: onlineProfile } = res;

      await profileService.post(`/profiles`, {
        cmdUri: options.cmdUri,
        sourceName: options.brand,
        onlineProfileId: onlineProfile.profileId,
        email: onlineProfile.emailAddress,
        phone: onlineProfile.shippingAddress[0]?.dayTelephone,
        firstName,
        lastName,
      });
    }

    dispatch({
      type: actionTypes.UPDATE_ONLINE_ACCOUNT_SUCCESS,
      payload: { ...res.data, sourceName: options.brand },
    });

    return res.data;
  } catch (err) {
    dispatch({
      type: actionTypes.UPDATE_ONLINE_ACCOUNT_FAILURE,
      payload: err,
    });

    throw err;
  }
};

export const fetchClients = () => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_LIST_LOADING,
  });

  try {
    const res = await clientsService.get('/customers');
    dispatch({ type: actionTypes.CLIENTS_LIST_SUCCESS, payload: res.data });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_LIST_ERROR,
      payload:
        error.response?.data?.message || error?.message || 'Not able to get client list, please try again later.',
    });
  }
};

export const setFilter = (value) => {
  return {
    type: actionTypes.CLIENTS_SET_FILTER,
    payload: value,
  };
};

export const setClientsSearch = (value) => {
  return {
    type: actionTypes.CLIENTS_SET_SEARCH,
    payload: value,
  };
};

export const searchCentralClients = (
  search,
  field = SEARCH_BY_FILED.PHONE,
  searchLevel = SEARCH_LEVEL.CLIENT_LIST,
  page = 1,
  withAddClientButton,
) => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_CENTRAL_SEARCH_LOADING,
  });
  cancelRequest.cancel();
  let showAddProfileButton = false;
  try {
    let res;
    if (search) {
      res = await clientsCentralSearchService.get('/customerSearch', {
        cancelable: true,
        params: {
          q: search,
          field,
          searchLevel,
          page,
        },
      });

      const shouldSendClientListRequest =
        withAddClientButton && searchLevel === SEARCH_LEVEL.GLOBAL && page === 1 && res.data?.data.length === 0;

      if (shouldSendClientListRequest) {
        const clientListRes = await clientsCentralSearchService.get('/customerSearch', {
          cancelable: true,
          params: {
            q: search,
            field,
            searchLevel: SEARCH_LEVEL.CLIENT_LIST,
            page,
          },
        });

        if (clientListRes.data?.data.length === 0) {
          showAddProfileButton = true;
        }
      }
    } else {
      res = await clientsCentralSearchService.get(`/myCustomers?page=${page}`);
    }

    dispatch({
      type: actionTypes.CLIENTS_CENTRAL_SEARCH_SUCCESS,
      payload: {
        ...res.data,
        showAddProfileButton,
        page,
      },
    });
  } catch (error) {
    if (error.toString() === 'Cancel') {
      return;
    }

    dispatch({
      type: actionTypes.CLIENTS_CENTRAL_SEARCH_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get client central list, please try again later.',
    });
  }
};

export const getGlobalClients = (
  search = '',
  params = {
    from: 0,
    size: 24,
    searchField: 'name',
  },
  withAddClientButton,
) => async (dispatch, getState) => {
  const currentState = getState();
  let showAddProfileButton = false;
  if (isEmpty(currentState.clientsModule.clientsList.clients)) {
    dispatch({ type: actionTypes.CLIENTS_INFINITE_LIST_LOADING, data: true });
  }

  try {
    const {
      data: { data },
    } = await clientsCentralSearchServiceV2.get(
      `/customers?q=${search}&size=${params.size}&from=${params.from}&field=${params.searchField}`,
    );
    const customers = {};
    const shouldSendClientListRequest = withAddClientButton && params.from === 0 && data.customers.length === 0;
    if (shouldSendClientListRequest) {
      const clientListRes = await groupsV2Service.get(`/groups/clientList/customers`, {
        params: {
          ...params,
          q: search,
        },
      });

      if (clientListRes.data?.data?.customers.length === 0) {
        showAddProfileButton = true;
      }
    }
    // Customers array is transformed into map with relevant elements positions
    // (because we can load customers from 0 to 30, and after it from 100 to 120, and they should be saved appropriately
    for (let i = params.from, j = 0; i <= params.from + params.size; i += 1, j += 1) {
      if (data.customers[j]) {
        customers[i] = data.customers[j];
      }
    }

    const totalCount = data.total > MAX_DISPLAY_SEARCH_RESULTS ? MAX_DISPLAY_SEARCH_RESULTS : data.total;

    dispatch({
      type: actionTypes.CLIENTS_INFINITE_LIST_SUCCESS,
      data: {
        customers,
        showAddProfileButton,
      },
    });
    dispatch({
      type: actionTypes.CLIENTS_INFINITE_LIST_STATS,
      data: {
        stats: { totalCount },
      },
    });
  } catch (error) {
    dispatch({
      type: actionTypes.GET_GROUP_CLIENTS_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get client central list, please try again later.',
    });
  }
};

export const getGroupClients = (groupId, search = '', page = 1) => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_CENTRAL_SEARCH_LOADING,
  });
  let res;

  try {
    if (groupId === DEFAULT_GROUP_DATA.groupId) {
      if (search) {
        res = await clientsCentralSearchService.get(`/customerSearch?q=${search}&page=${page}&searchLevel=clientList`);
      } else {
        res = await clientsCentralSearchService.get(`/myCustomers?page=${page}`);
      }
    } else {
      res = await groupsService.get(`/groups/${groupId}/customers`);
    }

    dispatch({ type: actionTypes.CLIENTS_CENTRAL_SEARCH_SUCCESS, payload: { ...res.data, page } });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_CENTRAL_SEARCH_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get client central list, please try again later.',
    });
  }
};

export const restoreGroupClients = (clientsState) => ({
  type: actionTypes.CLIENTS_LIST_RESTORE,
  payload: clientsState,
});

export const clearCentralClients = () => ({
  type: actionTypes.CLIENTS_CENTRAL_SEARCH_CLEAR,
});

export const fetchRecentClients = () => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_RECENT_LIST_LOADING,
  });

  try {
    const res = getRecentSearches();

    dispatch({ type: actionTypes.CLIENTS_RECENT_LIST_SUCCESS, payload: res });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_RECENT_LIST_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get recent client list, please try again later.',
    });
  }
};

export const updateRecentClients = (client) => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENTS_RECENT_LIST_LOADING,
  });

  try {
    const list = JSON.parse(window.localStorage.getItem('recentClients')) || []; // TODO rework to service

    if (!list.find((u) => u.profileId === client.profileId)) {
      list.push(client);
    }

    window.localStorage.setItem('recentClients', JSON.stringify(list));

    dispatch({ type: actionTypes.CLIENTS_RECENT_LIST_UPDATE, payload: list });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENTS_RECENT_LIST_ERROR,
      payload:
        error.response?.data?.message ||
        error?.message ||
        'Not able to get recent client list, please try again later.',
    });
  }
};

export const addShipping = (address) => async (dispatch) => {
  const { clientId, line2 = '', defaultAddress = false } = address;

  dispatch({
    type: actionTypes.ADD_SHIPPING_LOADING,
  });

  const body = {
    ...address,
    line2,
    defaultAddress,
  };

  try {
    await profileService.post(`customers/${clientId}/shipping`, body, { clientId });
    dispatch({
      type: actionTypes.ADD_SHIPPING_SUCCESS,
      payload: body,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.ADD_SHIPPING_ERROR,
      payload: error,
    });
  }
};

export const editShipping = (profileId, address, email, isDefault) => async (dispatch) => {
  dispatch({ type: actionTypes.EDIT_SHIPPING_LOADING });

  const {
    line1,
    line2 = '',
    firstName,
    lastName,
    city,
    state,
    zip,
    phone,
    defaultAddress,
    contactAddressName,
  } = address;

  try {
    const body = {
      email,
      line1,
      line2,
      firstName,
      lastName,
      city,
      state,
      zip,
      phone,
      contactAddressName,
      defaultAddress: isDefault || defaultAddress || false,
    };

    await profileService.put(`/customers/${profileId}/shipping`, body);

    dispatch({
      type: actionTypes.EDIT_SHIPPING_SUCCESS,
      payload: address,
    });
    return 'success';
  } catch (err) {
    dispatch({
      type: actionTypes.EDIT_SHIPPING_ERROR,
      payload: err,
    });
    return 'error';
  }
};

export const editPayments = (profileId, paymentMethod, isDefault) => async (dispatch) => {
  dispatch({ type: actionTypes.EDIT_PAYMENTS_LOADING });

  try {
    const body = { ...paymentMethod, defaultCard: isDefault ? true : paymentMethod.defaultCard };

    await profileService.put(`/customers/${profileId}/payments`, body);

    dispatch({
      type: actionTypes.EDIT_PAYMENTS_SUCCESS,
      payload: body,
    });
    return 'success';
  } catch (err) {
    dispatch({
      type: actionTypes.EDIT_PAYMENTS_ERROR,
      payload: err,
    });
    return 'error';
  }
};

export const changePaymentsErrorReset = () => ({ type: actionTypes.CHANGE_PAYMENTS_ERROR_RESET });

const zeroDollarAuth = async (payment = {}, clientId) => {
  try {
    const { data } = await paymentService.post(
      '/submitPayment',
      {
        oneTimeToken: payment.onTimeToken,
        billingAddress: {
          addressLine1: payment.billingAddress.line1,
          addressLine2: payment.billingAddress.line2,
          country: payment.billingAddress.country,
          city: payment.billingAddress.city,
          firstName: payment.billingAddress.firstName,
          lastName: payment.billingAddress.lastName,
          zip: payment.billingAddress.zip,
          state: payment.billingAddress.state,
        },
        cardType: payment.networkType,
        productTotalAmount: 0.0,
      },
      {
        httpClientOptions: { handleErrors: false },
        clientId,
      },
    );

    const isPLC = payment.networkType === 'PLC';

    return {
      data: {
        keyIdentifier: data.keyIdentifier,
        invoiceNumber: data.invoiceNumber,
        aurusPayTicketNumber: data.aurusPayTicketNumber,
        networkType: isPLC ? data.aurusPaymentInformation.subCardType : payment.networkType,
      },
    };
  } catch (err) {
    const errMsg = err.response?.data?.message || 'Failed zero payment check. Try another card.';
    return { error: errMsg };
  }
};

export const changePayments = (selectedPayment = {}, newCard = false) => async (dispatch, getState) => {
  const { clientsModule, form } = getState();

  if (isEmpty(selectedPayment)) return;

  let zeroDollarResponse = {};
  let updatedSelectedPayment = {};

  dispatch({ type: actionTypes.CHANGE_PAYMENTS_LOADING });

  if (form?.billingAddress?.submitFailed) {
    dispatch({ type: actionTypes.CHANGE_PAYMENTS_ERROR, payload: 'Incorrect billing address.' });
    return;
  }

  const clientDetails = clientsModule?.clientDetails?.data || {};
  const { clientId } = clientDetails;

  if (newCard) {
    zeroDollarResponse = await zeroDollarAuth(selectedPayment, clientId);
  }

  if (isEmpty(zeroDollarResponse.data) && newCard) {
    dispatch({ type: actionTypes.CHANGE_PAYMENTS_ERROR, payload: zeroDollarResponse.error });
  } else {
    const payments = clientDetails.payments || [];
    const foundPayment = payments.find((p) => p.maskedNumber === selectedPayment.maskedNumber);

    const updatedPayments = payments.map((p) =>
      foundPayment && p.maskedNumber === foundPayment.maskedNumber
        ? selectedPayment
        : {
            ...p,
            defaultCard: selectedPayment.defaultCard ? false : p.defaultCard,
            selected: selectedPayment.selected ? false : p.selected,
          },
    );

    if (!foundPayment) {
      updatedSelectedPayment = {
        ...selectedPayment,
        ...zeroDollarResponse.data,
      };

      updatedPayments.push({
        ...updatedSelectedPayment,
        networkType: convertAurusCardType(selectedPayment.networkType),
      });
    }

    try {
      if (newCard) {
        await profileService.post(`/customers/${clientId}/payments`, foundPayment || updatedSelectedPayment, {
          httpClientOptions: { handleErrors: false },
          clientId,
        });
      }

      dispatch({
        type: actionTypes.CHANGE_PAYMENTS_SUCCESS,
        payload: updatedPayments,
      });
      return 'success';
    } catch (err) {
      dispatch({
        type: actionTypes.CHANGE_PAYMENTS_ERROR,
        payload: 'Not able to add card, please try again later',
      });
    }
  }
};

export const validateAddress = (address) => async (dispatch) => {
  dispatch({ type: actionTypes.CLIENT_ADDRESS_VALIDATION_LOADING });
  const xml = convertToXml(address);

  try {
    const { data } = await addressValidationService.post('', xml, {
      headers: {
        'Content-Type': 'text/xml',
      },
      httpClientOptions: {
        handleErrors: false,
      },
    });

    const payload = getStandardizeAddress(data);
    dispatch({
      type: actionTypes.CLIENT_ADDRESS_VALIDATION_SUCCESS,
      payload: payload?.outputAddress || {},
    });
  } catch (err) {
    dispatch({
      type: actionTypes.CLIENT_ADDRESS_VALIDATION_ERROR,
      payload: `Not able to validate address, please try again later. ${err}`,
    });
  }
};

export const getClientLoyaltyData = (allKiNumbers, plccKiNumbers, nonPlccKiNumbers) => async (dispatch) => {
  try {
    dispatch({
      type: actionTypes.CLIENT_LOYALTY_LOADING,
    });

    const params = {};

    if (allKiNumbers?.length) {
      params.kiNumbers = allKiNumbers.join(',');
    }

    const res = await profileService.get(`/loyalty`, { params });
    const { loyaltyAssets, nonPlccAssets } = splitPlccAndNoPlccKiNumbers(
      res.data.loyaltyAssets,
      plccKiNumbers,
      nonPlccKiNumbers,
    );

    dispatch({
      type: actionTypes.CLIENT_LOYALTY_SUCCESS,
      payload: {
        ...res.data,
        loyaltyAssets,
        nonPlccAssets,
      },
    });
    dispatch({
      type: actionTypes.ADD_LOYALTY_CARD_PAYMENTS,
      payload: res.data?.loyaltyAssets || {},
    });
  } catch (error) {
    dispatch({
      type: actionTypes.CLIENT_LOYALTY_ERROR,
      payload: error,
    });
  }
};

export const resetAddressValidation = () => ({
  type: actionTypes.CLIENT_ADDRESS_VALIDATION_RESET,
});

export const getSaltNumber = (cardNumber) => async (dispatch, getState) => {
  const { user = {} } = getState();
  const { nmid } = user.data || {};

  try {
    dispatch({
      type: actionTypes.GET_BARCODE_SALT_NUMBER_LOADING,
    });

    const response = await profileService.get(`/salt/?user=${nmid}&giftCardNumber=${cardNumber}`);

    if (response.data?.status === 'failed') {
      throw response.data;
    }

    dispatch({
      type: actionTypes.GET_BARCODE_SALT_NUMBER_SUCCESS,
      payload: response.data?.saltRes?.salt,
    });
  } catch (err) {
    const error = err.response?.data?.message || err.message;
    dispatch({
      type: actionTypes.GET_BARCODE_SALT_NUMBER_ERROR,
      payload: error,
    });
  }
};

export const clearCMDProfileData = () => ({
  type: actionTypes.CLEAR_CMD_PROFILE_DATA,
});

export const getMessagesList = (clientId, associateId) => async () => {
  try {
    const { data } = await conversationService.get(`getInboxDetails/${associateId}/ExternalEntity/${clientId}`);
    return data.Items || [];
  } catch (error) {
    return [];
  }
};

export const getProfileDataFromClientId = (clientId, brand) => async (dispatch) => {
  try {
    const {
      data: { cmdUri },
    } = await cmdProfileService.get(`/profiles/${clientId}?sourceName=${brand}`);

    dispatch(getCMDProfile(cmdUri));
  } catch (error) {
    dispatch({
      type: actionTypes.CMD_PROFILE_ERROR,
      payload: error,
    });
  }
};

export const createFullClientsProfile = (formValues) => async (dispatch) => {
  const maxMatchesAmount = 100;
  const { email, phone } = formValues;

  try {
    const clientSearchMatches = await clientsCentralSearchServiceV2.get('customersByContact', {
      params: {
        email,
        phone,
        size: maxMatchesAmount,
      },
    });
    const customers = clientSearchMatches.data?.data.customers || [];

    if (customers.length) {
      return {
        status: 'exists',
        data: { customers },
      };
    }
  } catch (e) {
    dispatch(
      addNotification({
        id: `create-profile-${Date.now()}`,
        message: 'Error during client profile creation. Existed profiles can not be retrieved.',
        type: 'error',
      }),
    );

    return {
      status: 'error',
    };
  }

  try {
    const res = await cmdProfileService.post('/profiles', formValues);

    dispatch(
      addNotification({
        id: `create-profile-${Date.now()}`,
        message: 'Client profile successfully created',
        type: 'success',
      }),
    );

    return {
      status: 'created',
      data: res.data,
    };
  } catch (e) {
    dispatch(
      addNotification({
        id: `create-profile-${Date.now()}`,
        message: 'Error during client profile creation',
        type: 'error',
      }),
    );

    return {
      status: 'error',
    };
  }
};

export const switchAllClientConnects = (completed) => async (dispatch, getState) => {
  dispatch({ type: actionTypes.CLIENT_CONNECTS_LOADING });

  try {
    const associateId = getState().user.data.employeeNumber;
    const connectsState = getState().clientsModule.clientDetails.connects.data;
    const timestamps = connectsState.map(({ timestamp }) => timestamp);
    await eventDetailsService.post(`/rtc/${associateId}/actions`, {
      associateId,
      timestamps,
      completed,
    });

    dispatch({
      type: actionTypes.SET_CLIENT_CONNECTS,
      payload: connectsState.map((connect) => ({
        ...connect,
        completed,
      })),
    });
  } catch (e) {
    addNotification({
      id: `create-profile-${Date.now()}`,
      message: `Can not change client connects.`,
      type: 'error',
    });
  }
};
